import { useEffect } from 'preact/hooks';
import { t } from '../lib/i18n';
import { createPortal } from 'preact/compat';
import { X, RotateCcw, Check } from 'lucide-preact';
import type { AppVersion } from '../lib/recent-apps';

interface VersionHistoryProps {
  open: boolean;
  versions: AppVersion[];
  /** appId of the version currently shown in the preview. */
  currentAppId: string;
  onRestore: (v: AppVersion) => void;
  onClose: () => void;
}

function relativeTime(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * Slide-in timeline of an app's versions. Every generation and refinement
 * is a restorable node; tapping a past version swaps it into the preview
 * (App.tsx keeps the full timeline, so refining from there just appends).
 */
export function VersionHistory({ open, versions, currentAppId, onRestore, onClose }: VersionHistoryProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  // Newest first, keeping each version's original index for numbering.
  const rows = versions.map((v, idx) => ({ v, idx })).reverse();

  // Portal to <body>: an ancestor's lingering animation transform would
  // otherwise trap this fixed-position drawer inside the content column.
  return createPortal(
    <div className="drawer-root" role="dialog" aria-modal="true" aria-label="Version history">
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer-panel vh-panel">
        <header className="vh-header">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold tracking-tight text-[color:var(--fg)]">
              Version history
            </h2>
            <p className="text-[12px] text-[color:var(--fg-muted)]">
              {versions.length} {versions.length === 1 ? 'version' : 'versions'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close version history" className="vh-close">
            <X size={16} />
          </button>
        </header>

        <ol className="vh-timeline">
          {rows.map(({ v, idx }) => {
            const isCurrent = v.appId === currentAppId;
            const title = v.kind === 'create' ? 'Original' : `Revision ${idx}`;
            return (
              <li key={v.appId} className={`vh-item ${isCurrent ? 'vh-item--current' : ''}`}>
                <span className="vh-node" aria-hidden="true">
                  {isCurrent && <Check size={10} strokeWidth={3.5} />}
                </span>
                <div className="vh-body">
                  <div className="vh-row">
                    <span className="vh-version">{title}</span>
                    <span className="vh-time">{relativeTime(v.at)}</span>
                  </div>
                  {/* The create version's label IS "Original" — identical to the
                      title above it, which read as a leaked placeholder
                      ("Original / Original"). Give the baseline a crafted
                      descriptor; revisions keep their instruction label. */}
                  <p className="vh-label">{v.kind === 'create' ? 'Your first build' : v.label}</p>
                  {isCurrent ? (
                    <span className="vh-tag">{t('vh.currentlyViewing')}</span>
                  ) : (
                    <button
                      type="button"
                      className="vh-restore"
                      onClick={() => {
                        onRestore(v);
                        onClose();
                      }}
                    >
                      <RotateCcw size={12} />
                      Restore this version
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        {/* A single-version drawer was ~85% empty void. An inviting hint makes
            the one-build state read as intentional, not an unfinished list. */}
        {versions.length === 1 && (
          <p className="vh-hint">
            Make an edit with Magic Wand and each version lands here, tap any one to roll back.
          </p>
        )}
      </aside>
    </div>,
    document.body
  );
}
